import React, { useEffect, useMemo, useState } from "react";
import {
  Drawer,
  Row,
  Col,
  Form,
  Input,
  InputNumber,
  Select,
  DatePicker,
  Button,
  Space,
  Divider,
  Table,
  message,
} from "antd";
import axios from "axios";
import dayjs from "dayjs";

export default function ExpenseDrawer({ open, onClose, onSuccess, editData }) {
  const [form] = Form.useForm();
  const [roomAllocations, setRoomAllocations] = useState([]);
  const [assetAllocations, setAssetAllocations] = useState([]);
  const expenseType = Form.useWatch("type", form);
  const isEdit = !!editData;

  useEffect(() => {}, [expenseType]);
  /**
   * 初始化数据
   */
  useEffect(() => {
    if (open && editData) {
      form.setFieldsValue({
        ...editData,
        expenseDate: dayjs(editData.expenseDate),
        businessDate: dayjs(editData.businessDate),
      });

      setRoomAllocations(editData.roomAllocations || []);
      setAssetAllocations(editData.assetAllocations || []);
    }

    if (open && !editData) {
      form.resetFields();
      form.setFieldsValue({
        expenseDate: dayjs(),
      });
      setRoomAllocations([]);
      setAssetAllocations([]);
    }
  }, [open, editData]);

  /**
   * 总金额计算
   */
  const totalAllocation = useMemo(() => {
    const roomTotal = roomAllocations.reduce(
      (sum, i) => sum + Number(i.amount || 0),
      0,
    );

    const assetTotal = assetAllocations.reduce(
      (sum, i) => sum + Number(i.amount || 0),
      0,
    );

    return roomTotal + assetTotal;
  }, [roomAllocations, assetAllocations]);

  /**
   * 添加房源分摊
   */
  const addRoom = () => {
    setRoomAllocations((prev) => [
      ...prev,
      {
        room: null,
        amount: 0,
        startDate: null,
        endDate: null,
      },
    ]);
  };

  /**
   * 添加资产分摊
   */
  const addAsset = () => {
    setAssetAllocations((prev) => [
      ...prev,
      {
        asset: null,
        amount: 0,
        content: "",
      },
    ]);
  };

  /**
   * 更新房源
   */
  const updateRoom = (index, key, value) => {
    const next = [...roomAllocations];
    next[index][key] = value;
    setRoomAllocations(next);
  };

  /**
   * 更新资产
   */
  const updateAsset = (index, key, value) => {
    const next = [...assetAllocations];
    next[index][key] = value;
    setAssetAllocations(next);
  };

  /**
   * 删除
   */
  const removeRoom = (index) => {
    setRoomAllocations((prev) => prev.filter((_, i) => i !== index));
  };

  const removeAsset = (index) => {
    setAssetAllocations((prev) => prev.filter((_, i) => i !== index));
  };

  /**
   * 提交
   */
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      const payload = {
        ...values,
        expenseDate: values.expenseDate.toDate(),
        businessDate: values.businessDate.toDate(),
        roomAllocations,
        assetAllocations,
      };

      if (Math.abs(totalAllocation - Number(values.amount)) > 0.01) {
        message.error("分摊金额必须等于总金额");
        return;
      }

      if (isEdit) {
        await axios.put(`/api/expense/${editData._id}`, payload);
      } else {
        await axios.post("/api/expense", payload);
      }

      message.success("保存成功");
      onSuccess?.();
      onClose();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <Drawer
      title={isEdit ? "编辑支出" : "新增支出"}
      open={open}
      onClose={onClose}
      width={720}
      destroyOnClose
      extra={
        <Space>
          <Button onClick={onClose}>取消</Button>
          <Button type="primary" onClick={handleSubmit}>
            保存
          </Button>
        </Space>
      }
    >
      {/* ================= 基础信息（两列两行） ================= */}
      <Form
        layout="horizontal"
        labelCol={{ span: 6 }}
        wrapperCol={{ span: 18 }}
        form={form}
      >
        <Row gutter={16}>
          {/* 第1行左 */}
          <Col span={12}>
            <Form.Item
              label="费用名称"
              name="name"
              rules={[{ required: true }]}
              labelCol={{ span: 6 }}
              wrapperCol={{ span: 18 }}
            >
              <Input />
            </Form.Item>
          </Col>
          {/* 第2行左 */}

          <Col span={12}>
            <Form.Item
              label="票据号"
              name="invoiceNo"
              rules={[{ required: true }]}
              labelCol={{ span: 6 }}
              wrapperCol={{ span: 18 }}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="费用类型"
              name="type"
              rules={[{ required: true }]}
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <Select>
                <Select.Option value="RENT">房租</Select.Option>
                <Select.Option value="PROPERTY">物业费</Select.Option>
                <Select.Option value="REPAIR">维修</Select.Option>
                <Select.Option value="PURCHASE">采购</Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="总金额"
              name="amount"
              rules={[{ required: true }]}
              labelCol={{ span: 10 }}
              wrapperCol={{ span: 14 }}
            >
              <InputNumber style={{ width: "100%" }} min={0} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="支付日期"
              name="expenseDate"
              rules={[{ required: true }]}
              labelCol={{ span: 10 }}
              wrapperCol={{ span: 14 }}
            >
              <DatePicker style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              label="备注"
              name="remark"
              rules={[{ required: true }]}
              labelCol={{ span: 4 }}
              wrapperCol={{ span: 20 }}
            >
              <Input.TextArea />
            </Form.Item>
          </Col>
        </Row>
      </Form>
      {/* <Divider /> */}

      {/* ================= 分摊控制面板 ================= */}
      <div
        style={{
          marginBottom: 16,
          padding: 12,
          border: "1px solid #f0f0f0",
          borderRadius: 8,
          background: "#fafafa",
        }}
      >
        {/* 合计区域 */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 10,
          }}
        >
          <div style={{ fontWeight: 500 }}>当前分摊合计</div>

          <div
            style={{
              fontSize: 16,
              fontWeight: 600,
              color:
                totalAllocation === 0
                  ? "#999"
                  : Math.abs(totalAllocation - form.getFieldValue("amount")) <
                      0.01
                    ? "#52c41a"
                    : "#ff4d4f",
            }}
          >
            ￥ {totalAllocation.toFixed(2)}
          </div>
        </div>

        {/* 操作按钮区 */}
        <div style={{ display: "flex", gap: 8 }}>
          <Button onClick={addRoom} type="dashed" style={{ flex: 1 }}>
            + 添加房源分摊
          </Button>

          <Button onClick={addAsset} type="dashed" style={{ flex: 1 }}>
            + 添加资产分摊
          </Button>
        </div>
      </div>

      {roomAllocations.map((item, index) => (
        <Row
          key={index}
          gutter={8}
          align="middle"
          wrap={false}
          style={{
            marginTop: 8,
            width: "100%",
          }}
        >
          {/* 房间 */}
          <Col span={9} style={{ minWidth: 0 }}>
            <Select
              placeholder="房间ID"
              value={item.room}
              style={{ width: "100%" }}
              onChange={(value) => updateRoom(index, "room", value)}
            />
          </Col>

          {/* 金额 */}
          <Col span={4} style={{ minWidth: 0 }}>
            <InputNumber
              placeholder="金额"
              value={item.amount}
              style={{ width: "100%" }}
              onChange={(v) => updateRoom(index, "amount", v)}
            />
          </Col>

          {/* 开始 */}
          <Col span={5} style={{ minWidth: 0 }}>
            <DatePicker
              placeholder="开始"
              style={{ width: "100%" }}
              onChange={(v) => updateRoom(index, "startDate", v)}
            />
          </Col>

          {/* 结束 */}
          <Col span={5} style={{ minWidth: 0 }}>
            <DatePicker
              placeholder="结束"
              style={{ width: "100%" }}
              onChange={(v) => updateRoom(index, "endDate", v)}
            />
          </Col>

          {/* 删除 */}
          <Col
            span={1}
            style={{
              minWidth: 0,
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Button danger size="small" onClick={() => removeRoom(index)}>
              ×
            </Button>
          </Col>
        </Row>
      ))}

      {/* ================= 资产分摊 ================= */}
      {/* <Divider>资产分摊</Divider> */}

      {assetAllocations.map((item, index) => (
        <Space key={index} style={{ display: "flex", marginTop: 8 }}>
          <Select
            style={{ width: 180 }}
            placeholder="资产ID"
            value={item.asset}
            onChange={(e) => updateAsset(index, "asset", e.target.value)}
          />

          <InputNumber
            style={{ width: 180 }}
            placeholder="金额"
            value={item.amount}
            onChange={(v) => updateAsset(index, "amount", v)}
          />

          <Input
            style={{ width: 180 }}
            placeholder="维修内容"
            value={item.content}
            onChange={(e) => updateAsset(index, "content", e.target.value)}
          />

          <Button danger onClick={() => removeAsset(index)}>
            删除
          </Button>
        </Space>
      ))}
    </Drawer>
  );
}
